import { SidebarToggleButton } from './shared/SidebarToggleButton';
import { useSidebarAccessibility } from '../hooks/useSidebarAccessibility';
import { PageRuntimeBoundary } from './shared/PageRuntimeBoundary';
import { ApplicationBrand } from './shared/ApplicationBrand';
import { PageTitle, withPageTitles } from './shared/PageTitle';
import { ROUTES } from '../config/routes';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useCompany } from '../context/CompanyContext';
import React, { useState, useEffect, Suspense, useMemo, useCallback } from 'react';
import { Link, Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from '../../../../packages/ui/src/hooks/useTranslation';
import { Icon } from '../../../../packages/ui/src/components/icons/Icon';
import { Loading } from './Loading';
import { appConfigs } from '../config/app-configs';
import { moduleRegistry } from '../modules/ModuleRegistry';
import './Layout.css';
import { APP_VERSION, APP_COPYRIGHT } from '../config/version';
import { AppsMenuButton } from './shared/AppsMenuButton';
import { TopHeaderToolbar } from './shared/TopHeaderToolbar';
import type { TopHeaderToolbarHelpConfig } from './shared/TopHeaderToolbar';
import { useGlobalVoiceAssistantLayout } from './shared/useGlobalVoiceAssistantLayout';
import type { LayoutOutletContext } from './shared/useLayoutPageTitle';
import { useApps, pickLocalizedAppLabel } from '../context/AppsContext';
import { getApplicationCodeForApi } from '../config/app.config';
import { ProfileDropdown } from './shared/ProfileDropdown';
import { SidebarNav } from './shared/SidebarNav';
import { getDefaultUserSettingsUrl } from '../utils/coreAssetUrl';
import axios from '../api/axios';

/** ERP kabuğu: URL modülü → merkezi `GET /api/apps` modül/ana ürün kodu */
const ERP_SIDEBAR_REGISTRY_CODE: Record<string, string> = {
  finance: 'FINANCE',
  inventory: 'INVENTORY',
  sales: 'SALES',
  procurement: 'PROCUREMENT',
  manufacturing: 'MANUFACTURING',
  assets: 'ASSETS',
  import: 'ERP',
  export: 'ERP',
  budget: 'ERP',
  approval: 'ERP',
  consent: 'ERP',
  settings: 'ERP',
  accounting: 'ERP',
  default: 'ERP',
};

/** Üst header `<h1>`: gösterge paneli (dashboard) sayfalarında başlık gösterme */
function shouldHideDashboardPageTitle(path: string): boolean {
  const normalized = path.replace(/\/$/, '') || '/';
  if (normalized === '/dashboard') return true;
  if (/\/dashboard$/i.test(normalized)) return true;
  const parts = normalized.split('/').filter(Boolean);
  const moduleParts = parts[0] === 'erp' ? parts.slice(1) : parts;
  if (moduleParts.length === 1 && isSimpleShellModule(moduleParts[0])) {
    return true;
  }
  return false;
}

function isSimpleShellModule(moduleId: string): boolean {
  return moduleRegistry.getRegisteredModuleIds().includes(moduleId);
}

function getModuleIdFromPath(path: string): string {
  const parts = path.split('/').filter(Boolean);
  return parts[0] === 'erp' ? (parts[1] ?? '') : (parts[0] ?? '');
}

function getSimpleShellPageTitle(moduleId: string, path: string, dashboardLabel: string): string | null {
  const prefix = `/${moduleId}`;
  const erpPrefix = `/erp/${moduleId}`;
  if (
    path === prefix ||
    path.startsWith(`${prefix}/dashboard`) ||
    path === erpPrefix ||
    path.startsWith(`${erpPrefix}/dashboard`)
  ) {
    return dashboardLabel;
  }
  return null;
}

// Sidebar Header Component - Memoize edilmiş, gereksiz re-render'ları önler
const SidebarHeader = React.memo(({
  tenantLabel,
  isCollapsed,
  onCloseMobileMenu,
  homePath,
  title,
  appName
}: {
  tenantLabel?: string | null;
  isCollapsed: boolean;
  onCloseMobileMenu: () => void;
  homePath: string;
  title: string;
  appName?: string;
}) => {
  const displayText = appName ?? 'ERP';
  const ariaLabel = appName ? `${appName}` : 'QubePro ERP';

  return (
    <Link
      to={homePath}
      className="sidebar-header organization-logo-link"
      onClick={() => onCloseMobileMenu()}
      title={title}
    >
      <div className="organization-logo" role="img" aria-label={ariaLabel}>
        <ApplicationBrand label={displayText} />
      </div>
      {!isCollapsed && tenantLabel ? (
        <p className="tenant-name">{tenantLabel}</p>
      ) : null}
    </Link>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - sadece gerçekten değişen prop'lar için re-render
  return (
    prevProps.tenantLabel === nextProps.tenantLabel &&
    prevProps.isCollapsed === nextProps.isCollapsed &&
    prevProps.title === nextProps.title &&
    prevProps.appName === nextProps.appName &&
    prevProps.homePath === nextProps.homePath &&
    prevProps.onCloseMobileMenu === nextProps.onCloseMobileMenu
  );
});

SidebarHeader.displayName = 'SidebarHeader';

function Layout() {
  const { user, language, updateLanguage } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const sidebarHomePath = moduleRegistry.getModuleRootPath(location.pathname);
  const [layoutPageTitle, setLayoutPageTitle] = useState('');
  const layoutOutletContext = useMemo<LayoutOutletContext>(() => ({ setLayoutPageTitle }), []);
  const { isLoading: companyLoading, selectedCompany } = useCompany();
  const [searchParams] = useSearchParams();
  const isEmbedded = searchParams.get('embedded') === '1';

  // Sidebar collapse durumunu localStorage'dan oku
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('qubepro_sidebar_collapsed');
    return saved === 'true';
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const sidebarAccessibility = useSidebarAccessibility(Boolean(isMobileMenuOpen), Boolean(isCollapsed));
  const closeMobileMenu = useCallback((e?: React.MouseEvent) => {
    // Sadece mobil menü açıksa kapat
    if (isMobileMenuOpen) {
      if (e) {
        e.stopPropagation();
        // Mobil menü açıksa preventDefault çağır, yoksa Link'in normal davranışına izin ver
        e.preventDefault();
      }
      setIsMobileMenuOpen(false);
    }
  }, [isMobileMenuOpen]);

  const t = useTranslation(language);
  const { getApp } = useApps();
  const localized = useMemo(() => {
    const map: Record<string, { products: string; categories: string; dashboard: string; darkMode: string; lightMode: string }> = {
      tr: { products: 'Ürünler', categories: 'Kategoriler', dashboard: 'Gösterge Paneli', darkMode: 'Karanlık Mod', lightMode: 'Aydınlık Mod' },
      en: { products: 'Products', categories: 'Categories', dashboard: 'Dashboard', darkMode: 'Dark Mode', lightMode: 'Light Mode' },
      ar: { products: 'المنتجات', categories: 'الفئات', dashboard: 'لوحة التحكم', darkMode: 'الوضع الداكن', lightMode: 'الوضع الفاتح' },
      de: { products: 'Produkte', categories: 'Kategorien', dashboard: 'Dashboard', darkMode: 'Dunkelmodus', lightMode: 'Hellmodus' },
      fr: { products: 'Produits', categories: 'Categories', dashboard: 'Tableau de bord', darkMode: 'Mode sombre', lightMode: 'Mode clair' },
      es: { products: 'Productos', categories: 'Categorias', dashboard: 'Panel', darkMode: 'Modo oscuro', lightMode: 'Modo claro' },
      it: { products: 'Prodotti', categories: 'Categorie', dashboard: 'Dashboard', darkMode: 'Modalita scura', lightMode: 'Modalita chiara' },
      ru: { products: 'Товары', categories: 'Категории', dashboard: 'Панель', darkMode: 'Темная тема', lightMode: 'Светлая тема' },
      zh: { products: '产品', categories: '分类', dashboard: '仪表盘', darkMode: '深色模式', lightMode: '浅色模式' },
      ja: { products: '商品', categories: 'カテゴリ', dashboard: 'ダッシュボード', darkMode: 'ダークモード', lightMode: 'ライトモード' },
    };
    return map[language] || map.en;
  }, [language]);

  // URL'den uygulama ID'sini çıkar (/inventory -> inventory, /accounting -> accounting, diğerleri -> default)
  const currentAppId = useMemo(() => {
    const moduleId = getModuleIdFromPath(location.pathname);
    return isSimpleShellModule(moduleId) ? moduleId : 'default';
  }, [location.pathname]);

  /** Üst “View all” / arama: göreli `navigate("notifications")` hatalı; `/:module/...` mutlak olmalı (örn. `/inventory/notifications`). */
  const topHeaderToolbarPathPrefix = useMemo(() => {
    const moduleId = getModuleIdFromPath(location.pathname);
    if (isSimpleShellModule(moduleId)) {
      return location.pathname.startsWith('/erp/') ? `/erp/${moduleId}` : `/${moduleId}`;
    }
    return '/inventory';
  }, [location.pathname]);

  // Mevcut uygulamanın config'ini al - önce modül registry'den, sonra appConfigs'den
  const currentAppConfig = useMemo(() => {
    // Modül registry'den config al
    const moduleConfig = moduleRegistry.getModuleConfig(currentAppId);
    if (moduleConfig) {
      return moduleConfig;
    }
    // appConfigs'e geri dön
    return appConfigs[currentAppId] || appConfigs.default;
  }, [currentAppId]);

  // HTML element'ine lang attribute'u ekle (date input takvim popup'ı için)
  useEffect(() => {
    const htmlLangMap: Record<string, string> = {
      tr: 'tr-TR',
      en: 'en-US',
      ar: 'ar',
      de: 'de-DE',
      fr: 'fr-FR',
      es: 'es-ES',
      it: 'it-IT',
      ru: 'ru-RU',
      zh: 'zh-CN',
      ja: 'ja-JP',
    };
    document.documentElement.lang = htmlLangMap[language] || 'en-US';
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  // Sidebar collapsed durumunu localStorage'a kaydet ve body'ye class olarak ekle
  useEffect(() => {
    localStorage.setItem('qubepro_sidebar_collapsed', String(isCollapsed));
    if (isCollapsed) {
      document.body.classList.add('sidebar-collapsed');
    } else {
      document.body.classList.remove('sidebar-collapsed');
    }
    return () => {
      document.body.classList.remove('sidebar-collapsed');
    };
  }, [isCollapsed]);

  // Ekran genişliği değiştiğinde mobil menüyü kapat
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileMenuOpen]);

  const toggleSidebar = () => {
    // Mobilde sidebar'ı aç/kapat, desktop'ta collapse et
    const isMobile = window.innerWidth <= 768;
    if (isMobile) {
      setIsMobileMenuOpen(!isMobileMenuOpen);
    } else {
      setIsCollapsed(!isCollapsed);
    }
  };

  // Location değiştiğinde mobil menüyü kapat (sadece mobil cihazlarda)
  useEffect(() => {
    if (isMobileMenuOpen && window.innerWidth <= 768) {
      setIsMobileMenuOpen(false);
    }
  }, [location.pathname]);

  // tenantLabel'ı memoize et - gereksiz re-render'ları önle
  const tenantLabel = useMemo(() => user?.tenantName, [user?.tenantName]);

  const DOCS_BASE_URL = (import.meta.env.VITE_DOCS_BASE_URL as string) || '/docs/erp';

  const helpConfig = useMemo<TopHeaderToolbarHelpConfig>(() => {
    const slug = location.pathname.replace(/\/+$/, '').replace(/^\/+/, '');
    return {
      docsBaseUrl: DOCS_BASE_URL,
      pageDocsUrl: slug ? `${DOCS_BASE_URL}/${slug}` : DOCS_BASE_URL,
      labels: {
        help: (t.common as any).help,
        helpCenter: (t.common as any).helpCenter,
        aboutPage: (t.common as any).helpAboutPage,
        documentation: (t.common as any).helpDocumentation,
        keyboardShortcuts: (t.common as any).helpKeyboardShortcuts,
        whatsNew: (t.common as any).helpWhatsNew,
        f1Hint: (t.common as any).helpF1Hint,
        noDocsForPage: (t.common as any).helpNoDocsForPage,
      },
    };
  }, [location.pathname, DOCS_BASE_URL, t]);

  // Modül / ürün adı: önce Core apps registry (`label`), sonra panel çevirisi, sonra config
  const sidebarAppName = useMemo(() => {
    const registryCode = ERP_SIDEBAR_REGISTRY_CODE[currentAppId] ?? getApplicationCodeForApi();
    const app = getApp(registryCode);
    const fromRegistry = pickLocalizedAppLabel(app, language);
    if (fromRegistry) return fromRegistry;
    const panelApps = (t as { panel?: { apps?: Record<string, { name?: string }> } }).panel?.apps;
    const translatedName = panelApps?.[currentAppId]?.name;
    if (translatedName) return translatedName;
    return currentAppConfig?.name ?? getApplicationCodeForApi();
  }, [getApp, currentAppId, language, t, currentAppConfig?.name]);

  // Sayfa başlığını pathname'e göre belirle - modül config'inden veya appConfigs'den
  const getPageTitle = useCallback(() => {
    const path = location.pathname;
    if (shouldHideDashboardPageTitle(path)) return '';
    if (isSimpleShellModule(currentAppId)) {
      const shellTitle = getSimpleShellPageTitle(currentAppId, path, t.sidebar.dashboard);
      if (shellTitle) return shellTitle;
    }

    // Önce modül config'indeki pageTitles'a bak
    if (currentAppConfig.pageTitles && currentAppConfig.pageTitles[path]) {
      return currentAppConfig.pageTitles[path];
    }

    // Tam eşleşme yoksa, path'in başlangıcına göre kontrol et
    for (const [configPath, title] of Object.entries(currentAppConfig.pageTitles || {})) {
      if (path.startsWith(configPath)) {
        return title;
      }
    }

    // Modül içindeki sayfalar için özel başlık
    if (currentAppId !== 'default') {
      if (path.includes('/products')) {
        return currentAppConfig.pageTitles?.[`/${currentAppId}/products`] || localized.products;
      }
      if (path.includes('/categories')) {
        return currentAppConfig.pageTitles?.[`/${currentAppId}/categories`] || localized.categories;
      }
    }

    // Varsayılan kontrol
    if (path === '/') return t.sidebar.dashboard;
    if (path === '/dashboard') return localized.dashboard;
    if (path.startsWith('/products')) return localized.products;
    if (path.startsWith('/categories')) return localized.categories;
    if (path.startsWith('/admin')) return t.sidebar.settings;
    if (/\/search\/?$/.test(path)) return 'Arama';
    if (/\/notifications\/?$/.test(path)) return 'Bildirimler';
    if (/\/activity-logs\/?$/.test(path) || path.includes('/settings/activity-logs')) {
      return (t as any).sidebar?.activityLogs || 'Aktivite Günlükleri';
    }

    return '';
  }, [location.pathname, currentAppConfig, currentAppId, t.sidebar.dashboard, t.sidebar.settings, localized, t]);

  // Sekme başlığı: merkezi registry adı (küçük harf)
  useEffect(() => {
    document.title = `QubePro ${sidebarAppName}`;
  }, [sidebarAppName]);

  const pageTitle = layoutPageTitle || getPageTitle();
  const globalVoiceAssistant = useGlobalVoiceAssistantLayout({
    user,
    language,
    pathname: location.pathname,
    search: location.search,
    pageTitle,
  });

  const topHeaderContent = (
    <header className="top-header">
      <div className="top-header-content">
        <div style={{ flex: 1, minWidth: 0 }}>
          <PageTitle pathname={location.pathname} dashboardPath={ROUTES.DASHBOARD}>{pageTitle}</PageTitle>
        </div>

        <div className="top-header-right-group">
          {isSimpleShellModule(getModuleIdFromPath(location.pathname)) ? (
            <TopHeaderToolbar
              searchPath={`${topHeaderToolbarPathPrefix}/search`}
              notificationsPath={`${topHeaderToolbarPathPrefix}/notifications`}
              notificationsTenantReady={!companyLoading && !!selectedCompany}
              searchPlaceholder={t.common.search}
              searchAriaLabel={t.common.search}
              notificationLabels={{
                title: (t.common as any).notificationsTitle,
                unread: (t.common as any).notificationsUnread,
                typesMuted: (t.common as any).notificationsTypesMuted,
                viewAll: (t.common as any).notificationsViewAll,
                preferences: (t.common as any).notificationsPreferences,
                mutedLabel: (t.common as any).notificationsMutedLabel,
                empty: (t.common as any).notificationsEmpty,
              }}
              helpConfig={helpConfig}
              aiAssistantConfig={globalVoiceAssistant.aiAssistantConfig}
              {...globalVoiceAssistant.toolbarProps}
            />
          ) : null}
          <AppsMenuButton />
          <ProfileDropdown
            adminConsoleUrl="/console"
            settingsUrl={getDefaultUserSettingsUrl()}
          />
        </div>
      </div>
    </header>
  );

  // Gömülü görünüm (Apps iframe): ürün kabuğu gizli, sadece sayfa içeriği görünür
  if (isEmbedded) {
    return (
      <div className="layout layout-embedded erp-app-scope">
        <main className="main-content">
          <Suspense fallback={<Loading />}>
            <PageRuntimeBoundary><Outlet context={layoutOutletContext} /></PageRuntimeBoundary>
          </Suspense>
        </main>
      </div>
    );
  }

  return (
    <div className="layout erp-app-scope">
      {/* Mobil overlay */}
      {isMobileMenuOpen && (
        <div className="mobile-overlay" onClick={closeMobileMenu}></div>
      )}

      <SidebarToggleButton
        className="sidebar-toggle"
        onToggle={toggleSidebar}
        style={{
          position: 'fixed',
          top: '24px',
          left: isCollapsed ? '55px' : '227px',
          zIndex: 1002
        }}
        ref={sidebarAccessibility.toggleRef}
        label={sidebarAccessibility.isMobileViewport ? (Boolean(isMobileMenuOpen) ? t.sidebar.toggleMenuClose : t.sidebar.toggleMenu) : (isMobileMenuOpen ? t.sidebar.toggleMenuClose : (isCollapsed ? t.sidebar.toggleMenu : t.sidebar.toggleMenuClose))}
        controlsId="primary-sidebar"
        expanded={sidebarAccessibility.expanded}
        side={language === 'ar' ? 'right' : 'left'}
      />

      <nav
        className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobileMenuOpen ? 'mobile-open' : ''}`}
        onClick={(e) => {
          // Mobilde sidebar içindeki linklere tıklanınca menüyü kapat
          if (window.innerWidth <= 768 && e.target instanceof HTMLElement && e.target.closest('a')) {
            setIsMobileMenuOpen(false);
          }
        }} id="primary-sidebar" ref={sidebarAccessibility.sidebarRef}
      >
        <SidebarHeader
          tenantLabel={tenantLabel}
          isCollapsed={isCollapsed}
          onCloseMobileMenu={closeMobileMenu}
          homePath={sidebarHomePath}
          title={sidebarAppName}
          appName={sidebarAppName}
        />

        <div className="sidebar-user">
          {!isCollapsed && user && (
            <div className="user-details">
              <div className="user-info-row">
                <button
                  className="theme-toggle"
                  onClick={() => {
                    const nextTheme = theme === 'light' ? 'dark' : 'light';
                    toggleTheme();
                    axios.patch('/api/settings/user', { theme: nextTheme }).catch(() => { });
                  }}
                  title={theme === 'light' ? localized.darkMode : localized.lightMode}
                >
                  {theme === 'light' ? <Icon name="darkMode" size={18} /> : <Icon name="lightMode" size={18} />}
                </button>
                <div className="user-info-container">
                  <div className="user-name">{user.firstName} {user.lastName}</div>
                  <div className="user-email">{user.email}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        <SidebarNav
          isCollapsed={isCollapsed}
          closeMobileMenu={() => closeMobileMenu()}
          selectedCompanyId={selectedCompany?.id ? String(selectedCompany.id) : undefined}
          isCompanyContextLoading={companyLoading}
          manifestQuery={moduleRegistry.getRegisteredModuleIds().includes(currentAppId) && currentAppId !== 'settings' ? { module: currentAppId } : undefined}
        />

        <div className="sidebar-footer">
          {!isCollapsed && (
            <>
              <div className="sidebar-footer-info">
                <div className="sidebar-footer-name">QubePro {getApplicationCodeForApi()}</div>
                <div className="sidebar-footer-version">v{APP_VERSION}</div>
              </div>
              <div className="sidebar-footer-note">{APP_COPYRIGHT}</div>
            </>
          )}
        </div>
      </nav>

      <div className={`layout-content-wrapper ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
        {topHeaderContent}

        <main className="main-content">
          <Suspense fallback={<Loading />}>
            <PageRuntimeBoundary><Outlet context={layoutOutletContext} /></PageRuntimeBoundary>
          </Suspense>
        </main>
      </div>
      {globalVoiceAssistant.node}
    </div>
  );
}

export default withPageTitles(Layout);
